import type { RateCard } from '@/models/rateCard';

const norm = (value?: string) => value?.trim().toLowerCase() ?? '';

function rateCardIncludesKpi(rateCard: Pick<RateCard, 'kpis'>, kpi: string): boolean {
  const target = norm(kpi);
  return (rateCard.kpis ?? []).some((item) => norm(item) === target);
}

function formatsOverlap(cardFormats?: string[], selected?: string[]): boolean {
  if (!selected?.length) return true;
  if (!cardFormats?.length) return true;
  const cardSet = new Set((cardFormats ?? []).map(norm));
  return selected.some((format) => cardSet.has(norm(format)));
}

function allFormatsOnCard(cardFormats: string[] | undefined, selected: string[]): boolean {
  if (!selected.length) return false;
  const cardSet = new Set((cardFormats ?? []).map(norm));
  return selected.every((format) => cardSet.has(norm(format)));
}

export type RateCardBenchmarkLookup = {
  channel: string;
  product: string;
  kpi: string;
  formats?: string[];
};

/**
 * Resolve benchmark % from trader rate cards for a line item.
 * Prefer rows scoped to a single KPI (separate CTR / VTR rows) over multi-KPI rows.
 */
export function resolveRateCardBenchmark(
  rateCards: RateCard[],
  { channel, product, kpi, formats }: RateCardBenchmarkLookup
): number | undefined {
  if (!channel?.trim() || !product?.trim() || !kpi?.trim()) return undefined;

  const channelKey = norm(channel);
  const productKey = norm(product);

  const baseMatch = (requireFormatOverlap: boolean) =>
    rateCards.filter(
      (rc) =>
        norm(rc.channel) === channelKey &&
        norm(rc.product) === productKey &&
        rateCardIncludesKpi(rc, kpi) &&
        (requireFormatOverlap ? formatsOverlap(rc.formats, formats) : true) &&
        rc.benchmark != null &&
        Number.isFinite(Number(rc.benchmark))
    );

  let candidates = baseMatch(true);
  if (!candidates.length) {
    candidates = baseMatch(false);
  }
  if (!candidates.length) return undefined;

  const singleKpiRows = candidates.filter((rc) => (rc.kpis ?? []).length === 1);
  let pool = singleKpiRows.length > 0 ? singleKpiRows : candidates;

  if (formats?.length) {
    const exactFormatRows = pool.filter((rc) => allFormatsOnCard(rc.formats, formats));
    if (exactFormatRows.length > 0) {
      pool = exactFormatRows;
    }
  }

  const uniqueBenchmarks = [...new Set(pool.map((rc) => Number(rc.benchmark)))];
  if (uniqueBenchmarks.length > 1) {
    return undefined;
  }

  return uniqueBenchmarks[0];
}

export function hasConflictingRateCardBenchmarks(
  rateCards: RateCard[],
  lookup: RateCardBenchmarkLookup
): boolean {
  if (!lookup.channel?.trim() || !lookup.product?.trim() || !lookup.kpi?.trim()) return false;

  const channelKey = norm(lookup.channel);
  const productKey = norm(lookup.product);

  const candidates = rateCards.filter(
    (rc) =>
      norm(rc.channel) === channelKey &&
      norm(rc.product) === productKey &&
      rateCardIncludesKpi(rc, lookup.kpi) &&
      rc.benchmark != null &&
      Number.isFinite(Number(rc.benchmark))
  );

  const singleKpiRows = candidates.filter((rc) => (rc.kpis ?? []).length === 1);
  const pool = singleKpiRows.length > 0 ? singleKpiRows : candidates;
  const uniqueBenchmarks = new Set(pool.map((rc) => Number(rc.benchmark)));
  return uniqueBenchmarks.size > 1;
}
